import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { message } from 'antd';
import { useReactMediaRecorder } from 'react-media-recorder';
import FeatherIcon from 'feather-icons-react/build/FeatherIcon';
import Header from '../Header';
import Sidebar from '../Sidebar';
import teleChatApi from '../../api/telechat.api';
import { useWebSocket } from '../../contexts/WebSocketContext';
import AudioPlayer from './AudioPlayer';
import { StatusPill, EmptyState } from '../../ui';

// Allowed file extensions and max size
const ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'pdf', 'dcm', 'doc', 'docx', 'webm', 'mp3', 'm4a', 'ogg', 'wav'];
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB

// Status → design-system tone + Bulgarian label (shared with the doctor screens)
const STATUS = {
  PENDING_APPROVAL: { tone: 'warning', label: 'Чака одобрение' },
  ACTIVE:           { tone: 'success', label: 'Активна' },
  CLOSED:           { tone: 'neutral', label: 'Приключена' },
  DECLINED:         { tone: 'danger',  label: 'Отказана' },
};

// Chat surface styling — patient mirror of the doctor "Consultation Chat" design.
// Own (patient) messages right/indigo (.me); doctor left/white (.them);
// system pre-screening neutral/centered (.system).
const CHAT_CSS = `
  .pch-outer { flex: 1; min-height: 0; display: flex; justify-content: center; padding: var(--vc-space-4); }
  .pch-surface {
    width: 100%; max-width: var(--vc-container); display: flex;
    border-radius: var(--vc-radius-xl); overflow: hidden;
    box-shadow: var(--vc-shadow-lg); border: 1px solid var(--vc-color-border);
  }
  .pch-surface .vc-chat { flex: 1; }
  @media (max-width: 768px) { .pch-outer { padding: 0; } .pch-surface { border-radius: 0; border: none; box-shadow: none; } }

  .vc-chat {
    container-type: inline-size;
    font-family: var(--vc-font-sans); color: var(--vc-color-text);
    display: flex; flex-direction: column; height: 100%;
    background: var(--vc-color-bg);
  }

  /* ---- header (primary bar) ---- */
  .ch-head {
    flex: none; position: relative; overflow: hidden;
    background: var(--vc-color-primary); color: #fff;
    padding: var(--vc-space-4) var(--vc-space-5);
    display: flex; align-items: center; gap: var(--vc-space-3);
    box-shadow: var(--vc-shadow-sm); z-index: 2;
  }
  .ch-back {
    appearance: none; cursor: pointer; flex: none;
    display: inline-grid; place-items: center;
    width: 34px; height: 34px; border-radius: var(--vc-radius-md);
    color: #fff; background: rgba(255,255,255,.14); border: 1px solid rgba(255,255,255,.22);
  }
  .ch-back:hover { background: rgba(255,255,255,.24); }
  .ch-avatar {
    flex: none; width: 42px; height: 42px; border-radius: var(--vc-radius-pill);
    background: rgba(255,255,255,.18); box-shadow: inset 0 0 0 1px rgba(255,255,255,.28);
    color: #fff; display: grid; place-items: center;
    font-weight: var(--vc-weight-bold); font-size: var(--vc-text-sm); letter-spacing: .03em;
  }
  .ch-id { flex: 1 1 auto; min-width: 0; display: flex; flex-direction: column; gap: 3px; }
  .ch-name {
    font-size: var(--vc-text-md); font-weight: var(--vc-weight-semibold);
    line-height: var(--vc-leading-tight); color: #fff;
    white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
  }
  .ch-sub { display: flex; align-items: center; gap: var(--vc-space-3); flex-wrap: wrap; }
  .ch-spec { font-size: var(--vc-text-xs); color: rgba(255,255,255,.82); }
  /* Connection dot — reflects THIS session's own socket, not the doctor's presence. */
  .ch-net { flex: none; display: inline-flex; align-items: center; }
  .ch-net .dot { width: 9px; height: 9px; border-radius: var(--vc-radius-pill); flex: none; }
  .ch-net.ok .dot { background: #7bed9f; }
  .ch-net.wait .dot { background: #ffd479; }
  .ch-net.off .dot { background: #ffb3b3; }
  .ch-pill { flex: none; }

  /* ---- state chrome (banners) ---- */
  .ch-statebar { flex: none; padding: var(--vc-space-3) var(--vc-space-4); z-index: 1; }
  .banner { display: flex; align-items: flex-start; gap: var(--vc-space-3); border-radius: var(--vc-radius-lg); padding: var(--vc-space-3) var(--vc-space-4); }
  .banner .b-ico { flex: none; margin-top: 1px; }
  .banner .b-body { min-width: 0; }
  .banner .b-title { font-size: var(--vc-text-sm); font-weight: var(--vc-weight-semibold); }
  .banner .b-desc { font-size: var(--vc-text-sm); margin-top: 2px; line-height: var(--vc-leading-normal); color: var(--vc-color-text-soft); }
  .banner.info    { background: var(--vc-color-info-bg);    color: var(--vc-color-info); }
  .banner.warning { background: var(--vc-color-warning-bg); color: var(--vc-color-warning); }
  .banner.danger  { background: var(--vc-color-danger-bg);  color: var(--vc-color-danger); }

  /* ---- thread ---- */
  .ch-thread {
    flex: 1 1 auto; min-height: 0; overflow-y: auto;
    padding: var(--vc-space-4) var(--vc-space-5);
    display: flex; flex-direction: column; gap: var(--vc-space-3);
    -webkit-overflow-scrolling: touch;
  }
  @container (max-width: 460px) { .ch-thread { padding: var(--vc-space-4); } }

  .msg-row { display: flex; flex-direction: column; max-width: 78%; }
  .msg-row.me { align-self: flex-end; align-items: flex-end; }
  .msg-row.them { align-self: flex-start; align-items: flex-start; }
  .msg-row.system { align-self: flex-start; align-items: flex-start; }

  .bubble {
    border-radius: var(--vc-radius-lg); padding: var(--vc-space-3);
    font-size: var(--vc-text-base); line-height: var(--vc-leading-normal);
    box-shadow: var(--vc-shadow-sm); position: relative;
  }
  .msg-row.me .bubble { background: var(--vc-color-primary); color: #fff; border-bottom-right-radius: var(--vc-radius-sm); }
  .msg-row.them .bubble { background: var(--vc-color-surface); color: var(--vc-color-text); border: 1px solid var(--vc-color-border); border-bottom-left-radius: var(--vc-radius-sm); }
  .msg-row.system .bubble { background: var(--vc-color-neutral-bg); color: var(--vc-color-text-soft); border: 1px dashed var(--vc-color-border); box-shadow: none; border-bottom-left-radius: var(--vc-radius-sm); font-size: var(--vc-text-sm); }
  .bubble-name { font-size: var(--vc-text-xs); font-weight: var(--vc-weight-semibold); color: var(--vc-color-primary); margin-bottom: 4px; letter-spacing: .01em; }
  .system-tag { display: inline-flex; align-items: center; gap: 6px; font-size: var(--vc-text-xs); font-weight: var(--vc-weight-semibold); text-transform: uppercase; letter-spacing: .05em; color: var(--vc-color-neutral); margin-bottom: 6px; }
  .bubble-text { white-space: pre-wrap; word-break: break-word; }
  .bubble-time { display: block; font-family: var(--vc-font-mono); font-size: 10px; letter-spacing: .04em; margin-top: 5px; text-align: right; color: var(--vc-color-text-subtle); }
  .msg-row.me .bubble-time { color: rgba(255,255,255,.72); }

  /* ---- file chip inside bubble ---- */
  .file-chip {
    display: flex; align-items: center; gap: var(--vc-space-3);
    padding: var(--vc-space-2) var(--vc-space-3); border-radius: var(--vc-radius-md);
    background: var(--vc-color-surface-muted); border: 1px solid var(--vc-color-border);
    min-width: 200px; max-width: 260px;
  }
  .msg-row.me .file-chip { background: rgba(255,255,255,.14); border-color: rgba(255,255,255,.28); }
  .file-ico { flex: none; width: 38px; height: 38px; border-radius: var(--vc-radius-md); display: grid; place-items: center; color: #fff; }
  .file-ico.image { background: #7048e8; }
  .file-ico.pdf { background: #c92a2a; }
  .file-ico.doc { background: #1971c2; }
  .file-ico.generic { background: var(--vc-color-neutral); }
  .file-meta { min-width: 0; flex: 1 1 auto; display: flex; flex-direction: column; gap: 1px; }
  .file-name { font-size: var(--vc-text-sm); font-weight: var(--vc-weight-medium); white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
  .msg-row.me .file-name { color: #fff; }
  .file-size { font-family: var(--vc-font-mono); font-size: var(--vc-text-xs); color: var(--vc-color-text-subtle); }
  .msg-row.me .file-size { color: rgba(255,255,255,.7); }
  .file-dl {
    appearance: none; cursor: pointer; flex: none; display: inline-grid; place-items: center;
    width: 30px; height: 30px; border-radius: var(--vc-radius-pill);
    color: var(--vc-color-primary); background: var(--vc-color-primary-soft); border: none;
  }
  .msg-row.me .file-dl { color: #fff; background: rgba(255,255,255,.2); }
  .file-dl:hover { filter: brightness(.96); }

  /* ---- inline audio player wrapper ---- */
  .audio { display: flex; align-items: center; gap: var(--vc-space-3); min-width: 210px; max-width: 280px; padding: 4px; }
  .audio-loading { font-size: var(--vc-text-sm); color: inherit; opacity: .8; }

  /* ---- loading thread ---- */
  .sk-row { display: flex; gap: var(--vc-space-3); }
  .sk-row.right { justify-content: flex-end; }
  .sk-bubble { height: 46px; border-radius: var(--vc-radius-lg); background: linear-gradient(90deg, var(--vc-color-border-soft) 25%, var(--vc-color-surface-muted) 50%, var(--vc-color-border-soft) 75%); background-size: 200% 100%; animation: sk 1.3s ease infinite; }
  @keyframes sk { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }

  /* ---- composer ---- */
  .composer {
    flex: none; z-index: 2; border-top: 1px solid var(--vc-color-border);
    background: var(--vc-color-surface); padding: var(--vc-space-3) var(--vc-space-4);
    display: flex; flex-direction: column; gap: var(--vc-space-2);
  }
  .composer-preview {
    display: flex; align-items: center; gap: var(--vc-space-3);
    padding: var(--vc-space-2) var(--vc-space-3); background: var(--vc-color-primary-soft);
    border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-md);
  }
  .composer-preview .file-ico { width: 32px; height: 32px; }
  .composer-preview .rm {
    appearance: none; cursor: pointer; flex: none; margin-left: auto;
    display: inline-grid; place-items: center; width: 26px; height: 26px;
    border-radius: var(--vc-radius-pill); border: none;
    color: var(--vc-color-text-muted); background: var(--vc-color-bg);
  }
  .composer-preview .rm:hover { color: var(--vc-color-danger); background: var(--vc-color-danger-bg); }

  .composer-input-row { display: flex; align-items: flex-end; gap: var(--vc-space-2); }
  .composer-attach, .composer-send, .composer-mic {
    appearance: none; cursor: pointer; flex: none; width: 42px; height: 42px;
    border-radius: var(--vc-radius-pill); display: grid; place-items: center; border: none; transition: all .12s ease;
  }
  .composer-attach { color: var(--vc-color-text-muted); background: var(--vc-color-bg); border: 1px solid var(--vc-color-border); }
  .composer-attach:hover { color: var(--vc-color-primary); border-color: var(--vc-color-primary); }
  .composer-attach:disabled, .composer-send:disabled, .composer-mic:disabled { opacity: .6; cursor: default; }
  .composer-textwrap { flex: 1 1 auto; min-width: 0; }
  .composer-text {
    width: 100%; resize: none; font-family: inherit; font-size: var(--vc-text-base);
    color: var(--vc-color-text); line-height: var(--vc-leading-normal);
    padding: 10px var(--vc-space-3); max-height: 110px;
    border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-lg);
    background: var(--vc-color-bg); outline: none;
  }
  .composer-text::placeholder { color: var(--vc-color-text-subtle); }
  .composer-text:focus { border-color: var(--vc-color-primary); box-shadow: 0 0 0 3px var(--vc-color-primary-soft); }
  .composer-send { background: var(--vc-color-primary); color: #fff; }
  .composer-send:hover { filter: brightness(1.08); }
  .composer-mic { background: var(--vc-color-primary); color: #fff; }
  .composer-mic:hover { filter: brightness(1.08); }

  /* recording bar */
  .composer-recording { display: flex; align-items: center; gap: var(--vc-space-3); padding: 6px var(--vc-space-2); }
  .rec-dot { width: 12px; height: 12px; border-radius: var(--vc-radius-pill); background: var(--vc-color-danger); flex: none; animation: rec-blink 1s steps(1) infinite; }
  @keyframes rec-blink { 50% { opacity: .25; } }
  .rec-wave { flex: 1 1 auto; display: flex; align-items: center; gap: 3px; height: 24px; }
  .rec-wave span { flex: 1 1 auto; background: var(--vc-color-danger); opacity: .5; border-radius: 2px; animation: rec-bounce 1s ease-in-out infinite; }
  @keyframes rec-bounce { 0%,100% { height: 20%; } 50% { height: 100%; } }
  .rec-timer { font-family: var(--vc-font-mono); font-size: var(--vc-text-sm); font-weight: var(--vc-weight-medium); color: var(--vc-color-danger); flex: none; }
  .rec-stop {
    appearance: none; cursor: pointer; flex: none; display: inline-flex; align-items: center; gap: 6px;
    font-family: inherit; font-size: var(--vc-text-sm); font-weight: var(--vc-weight-semibold);
    color: var(--vc-color-danger); background: var(--vc-color-danger-bg);
    border: 1px solid #f3c2c2; border-radius: var(--vc-radius-md); padding: 8px var(--vc-space-3);
  }
  /* recorded bar */
  .composer-recorded { display: flex; align-items: center; gap: var(--vc-space-2); }
  .composer-recorded .audio { flex: 1 1 auto; min-width: 0; max-width: none; background: var(--vc-color-bg); border: 1px solid var(--vc-color-border); border-radius: var(--vc-radius-lg); padding: 4px var(--vc-space-2); }
  .rec-del, .rec-send { appearance: none; cursor: pointer; flex: none; width: 42px; height: 42px; border-radius: var(--vc-radius-pill); display: grid; place-items: center; border: none; }
  .rec-del { color: var(--vc-color-danger); background: var(--vc-color-danger-bg); }
  .rec-del:hover { filter: brightness(.97); }
  .rec-send { color: #fff; background: var(--vc-color-primary); }
  .rec-send:hover { filter: brightness(1.08); }
  .rec-del:disabled, .rec-send:disabled { opacity: .6; cursor: default; }

  .pch-spin { display: inline-flex; animation: pch-spin 1s linear infinite; }
  @keyframes pch-spin { to { transform: rotate(360deg); } }
`;

// --- helpers ---
const pad = (n) => String(n).padStart(2, '0');

// Doctor initials — strip a leading "Д-р " title
const initials = (name) => {
  const clean = (name || '').replace(/^Д-?р\s+/i, '').trim();
  const p = clean.split(/\s+/);
  return (((p[0]?.[0]) || '') + ((p[p.length - 1]?.[0]) || '')).toUpperCase() || 'Л';
};

const formatFileSize = (bytes) => {
  if (!bytes && bytes !== 0) return '';
  if (bytes < 1024) return bytes + ' B';
  if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(0) + ' KB';
  return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
};

const isAudioFile = (fileType) => fileType?.startsWith('audio/');

const fileKind = (fileType, fileName) => {
  if (fileType?.startsWith('image/')) return 'image';
  if (fileType === 'application/pdf' || fileName?.toLowerCase().endsWith('.pdf')) return 'pdf';
  if (isAudioFile(fileType)) return 'audio';
  const ext = fileName?.split('.').pop()?.toLowerCase();
  if (['doc', 'docx'].includes(ext)) return 'doc';
  return 'generic';
};

const fileIconName = (kind) => (kind === 'image' ? 'image' : (kind === 'pdf' || kind === 'doc') ? 'file-text' : 'file');

const PatientChatDetail = () => {
  const { chatId } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  const fileInputRef = useRef(null);
  const textAreaRef = useRef(null);
  const { subscribeToChatMessages, connectionState } = useWebSocket();

  const preventBlur = (e) => e.preventDefault();

  const [chatDetail, setChatDetail] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [newMessage, setNewMessage] = useState('');

  // File upload state
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [downloadingFiles, setDownloadingFiles] = useState({});
  const [audioUrls, setAudioUrls] = useState({});

  // Voice recording state
  const [recordingDuration, setRecordingDuration] = useState(0);
  const recordingIntervalRef = useRef(null);

  const getSupportedAudioType = () => {
    if (typeof MediaRecorder !== 'undefined') {
      if (MediaRecorder.isTypeSupported('audio/mp4')) return { mimeType: 'audio/mp4', extension: 'm4a' };
      if (MediaRecorder.isTypeSupported('audio/webm')) return { mimeType: 'audio/webm', extension: 'webm' };
      if (MediaRecorder.isTypeSupported('audio/mpeg')) return { mimeType: 'audio/mpeg', extension: 'mp3' };
    }
    return { mimeType: 'audio/mp4', extension: 'm4a' };
  };

  const audioFormat = getSupportedAudioType();

  const {
    status: recordingStatus,
    startRecording,
    stopRecording,
    mediaBlobUrl,
    clearBlobUrl,
    error: recordingError,
  } = useReactMediaRecorder({
    audio: true,
    blobPropertyBag: { type: audioFormat.mimeType },
  });

  const isRecording = recordingStatus === 'recording';

  useEffect(() => {
    if (recordingError) message.error(`Грешка при запис: ${recordingError}`);
  }, [recordingError]);

  useEffect(() => {
    loadChatDetail();
    loadMessages();
  }, [chatId]);

  // Refetch messages when page becomes visible (after phone unlock or app switch)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (!document.hidden && chatId) {
        await loadMessages();
        teleChatApi.markPatientChatRead(parseInt(chatId)).catch(() => {});
        setTimeout(scrollToBottom, 300);
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, [chatId]);

  // Subscribe to WebSocket messages for this chat
  useEffect(() => {
    if (!chatId) return;
    const unsubscribe = subscribeToChatMessages(parseInt(chatId), (event) => {
      if (event.type === 'MESSAGE_NEW') {
        setMessages((prev) => {
          const exists = prev.some((m) => m.messageId === event.payload.messageId);
          if (exists) return prev;
          return [...prev, event.payload];
        });
        teleChatApi.markPatientChatRead(parseInt(chatId)).catch(() => {});
      } else if (event.type === 'CHAT_CLOSED') {
        loadChatDetail();
      } else if (event.type === 'CHAT_APPROVED') {
        loadChatDetail();
        message.success('Консултацията е одобрена!');
      } else if (event.type === 'CHAT_DECLINED') {
        loadChatDetail();
        message.info('Консултацията е отказана.');
      }
    });
    return () => unsubscribe();
  }, [chatId, subscribeToChatMessages]);

  // Scroll to bottom on initial load
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  useEffect(() => {
    if (!loading && messages.length > 0 && !initialLoadDone) {
      scrollToBottom();
      setInitialLoadDone(true);
    }
  }, [loading, messages, initialLoadDone]);

  // Auto-scroll on new messages only if user is near the bottom
  useEffect(() => {
    if (!initialLoadDone) return;
    const el = messagesContainerRef.current;
    const nearBottom = el ? el.scrollHeight - el.scrollTop - el.clientHeight < 200 : true;
    if (nearBottom) scrollToBottom();
  }, [messages, initialLoadDone]);

  useEffect(() => {
    if (chatId) teleChatApi.markPatientChatRead(parseInt(chatId)).catch(() => {});
  }, [chatId]);

  const scrollToBottom = () => {
    const el = messagesContainerRef.current;
    if (el) el.scrollTop = el.scrollHeight;
    else messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputFocus = () => setTimeout(scrollToBottom, 300);

  const loadChatDetail = async () => {
    try {
      const data = await teleChatApi.getPatientChatDetail(parseInt(chatId));
      setChatDetail(data);
    } catch (error) {
      console.error('Error loading chat detail:', error);
      message.error('Грешка при зареждане на консултацията');
      navigate('/telechat');
    }
  };

  const loadMessages = async () => {
    try {
      setLoading(true);
      const data = await teleChatApi.getPatientMessages(parseInt(chatId));
      setMessages(data);
    } catch (error) {
      console.error('Error loading messages:', error);
      message.error('Грешка при зареждане на съобщенията');
    } finally {
      setLoading(false);
    }
  };

  const handleSendMessage = async () => {
    const hasText = newMessage.trim().length > 0;
    const hasFile = selectedFile !== null;
    if (!hasText && !hasFile) return;

    try {
      setSending(true);
      setUploading(hasFile);

      let fileData = null;
      if (hasFile) {
        try {
          fileData = await teleChatApi.uploadFile(parseInt(chatId), selectedFile, false);
        } catch (uploadError) {
          console.error('Error uploading file:', uploadError);
          message.error('Грешка при качване на файла');
          setUploading(false);
          setSending(false);
          return;
        }
      }

      await teleChatApi.sendPatientMessage(parseInt(chatId), {
        messageText: hasText ? newMessage.trim() : undefined,
        fileKey: fileData?.fileKey,
        fileName: fileData?.fileName,
        fileType: fileData?.fileType,
        fileSizeBytes: fileData?.fileSizeBytes,
      });

      setNewMessage('');
      setSelectedFile(null);
      if (textAreaRef.current) textAreaRef.current.style.height = 'auto';
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMsg = error?.data?.message || 'Грешка при изпращане на съобщението';
      message.error(errorMsg);
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const handleTextChange = (e) => {
    setNewMessage(e.target.value);
    const el = e.target;
    el.style.height = 'auto';
    el.style.height = Math.min(el.scrollHeight, 110) + 'px';
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const handleFileSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const extension = file.name.split('.').pop()?.toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      message.error(`Неподдържан файлов формат. Позволени: ${ALLOWED_EXTENSIONS.join(', ')}`);
      e.target.value = '';
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      message.error('Файлът е твърде голям. Максимален размер: 100 MB');
      e.target.value = '';
      return;
    }
    setSelectedFile(file);
    e.target.value = '';
  };

  const clearSelectedFile = () => setSelectedFile(null);

  const handleStartRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      stream.getTracks().forEach((track) => track.stop());
      setRecordingDuration(0);
      startRecording();
      recordingIntervalRef.current = setInterval(() => {
        setRecordingDuration((prev) => prev + 1);
      }, 1000);
    } catch (err) {
      console.error('Microphone permission error:', err);
      message.error('Моля, разрешете достъп до микрофона');
    }
  };

  const handleStopRecording = () => {
    stopRecording();
    if (recordingIntervalRef.current) {
      clearInterval(recordingIntervalRef.current);
      recordingIntervalRef.current = null;
    }
  };

  const handleCancelRecording = () => {
    handleStopRecording();
    clearBlobUrl();
    setRecordingDuration(0);
  };

  const handleSendVoiceMessage = async () => {
    if (!mediaBlobUrl) return;
    try {
      setSending(true);
      setUploading(true);
      const response = await fetch(mediaBlobUrl);
      const blob = await response.blob();
      const fileName = `voice_${Date.now()}.${audioFormat.extension}`;
      const file = new File([blob], fileName, { type: audioFormat.mimeType });
      const fileData = await teleChatApi.uploadFile(parseInt(chatId), file, false);
      await teleChatApi.sendPatientMessage(parseInt(chatId), {
        fileKey: fileData.fileKey,
        fileName: fileData.fileName,
        fileType: fileData.fileType,
        fileSizeBytes: fileData.fileSizeBytes,
      });
      clearBlobUrl();
      setRecordingDuration(0);
    } catch (error) {
      console.error('Error sending voice message:', error);
      message.error('Грешка при изпращане на гласовото съобщение');
    } finally {
      setSending(false);
      setUploading(false);
    }
  };

  const formatRecordingTime = (seconds) => `${Math.floor(seconds / 60)}:${pad(seconds % 60)}`;

  const handleDownloadFile = async (msg) => {
    if (!msg.fileUrl) return;
    const fileKey = msg.fileUrl;
    const newWindow = window.open('about:blank', '_blank');
    setDownloadingFiles((prev) => ({ ...prev, [msg.messageId]: true }));
    try {
      const downloadUrl = await teleChatApi.getDownloadUrl(parseInt(chatId), fileKey, false);
      if (newWindow) newWindow.location.href = downloadUrl;
      else window.location.href = downloadUrl;
    } catch (error) {
      console.error('Error getting download URL:', error);
      message.error('Грешка при изтегляне на файла');
      if (newWindow) newWindow.close();
    } finally {
      setDownloadingFiles((prev) => ({ ...prev, [msg.messageId]: false }));
    }
  };

  const loadAudioUrl = async (msg) => {
    if (!msg.fileUrl || audioUrls[msg.messageId]) return;
    try {
      const downloadUrl = await teleChatApi.getDownloadUrl(parseInt(chatId), msg.fileUrl, false);
      setAudioUrls((prev) => ({ ...prev, [msg.messageId]: downloadUrl }));
    } catch (error) {
      console.error('Error loading audio URL:', error);
    }
  };

  const formatMessageTime = (dateString) => {
    if (!dateString) return '';
    const utcDateString = dateString.endsWith('Z') ? dateString : dateString + 'Z';
    return new Date(utcDateString).toLocaleTimeString('bg-BG', {
      hour: '2-digit', minute: '2-digit', timeZone: 'Europe/Sofia',
    });
  };

  const doctorName = chatDetail?.doctor?.fullName || 'Лекар';
  const doctorSpec = chatDetail?.doctor?.speciality;
  const statusInfo = STATUS[chatDetail?.status] || { tone: 'neutral', label: chatDetail?.status || '' };
  const netState = connectionState === 'CONNECTED' ? { cls: 'ok', label: 'Свързан' }
    : connectionState === 'DISCONNECTED' ? { cls: 'off', label: 'Няма връзка' }
    : { cls: 'wait', label: 'Свързване…' };
  const status = chatDetail?.status;
  const canSendMessage = status === 'ACTIVE';

  // --- render helpers ---
  const renderFileAttachment = (msg, mine) => {
    if (isAudioFile(msg.fileType)) {
      const audioUrl = audioUrls[msg.messageId];
      const color = mine ? '#fff' : 'var(--vc-color-primary)';
      if (!audioUrl) {
        loadAudioUrl(msg);
        return <div className="audio" style={{ color }}><span className="audio-loading">Зареждане...</span></div>;
      }
      return (
        <div className="audio" style={{ color }}>
          <AudioPlayer src={audioUrl} style={{ color, width: '100%' }} />
        </div>
      );
    }
    const kind = fileKind(msg.fileType, msg.fileName);
    return (
      <div className="file-chip" onClick={() => handleDownloadFile(msg)} style={{ cursor: 'pointer' }}>
        <span className={'file-ico ' + kind}><FeatherIcon icon={fileIconName(kind)} size={18} /></span>
        <div className="file-meta">
          <span className="file-name">{msg.fileName || 'Прикачен файл'}</span>
          {msg.fileSizeBytes ? <span className="file-size">{formatFileSize(msg.fileSizeBytes)}</span> : null}
        </div>
        <button
          className="file-dl"
          aria-label="Изтегли"
          onClick={(e) => { e.stopPropagation(); handleDownloadFile(msg); }}
        >
          {downloadingFiles[msg.messageId]
            ? <span className="pch-spin"><FeatherIcon icon="loader" size={15} /></span>
            : <FeatherIcon icon="download" size={15} />}
        </button>
      </div>
    );
  };

  const renderMessage = (msg) => {
    const hasFile = !!msg.fileUrl;

    // System pre-screening — neutral centered bubble
    if (msg.senderType === 'SYSTEM') {
      return (
        <div key={msg.messageId} className="msg-row system">
          <div className="bubble">
            <div className="system-tag"><FeatherIcon icon="help-circle" size={13} /> Въпросник</div>
            {msg.messageText && <div className="bubble-text">{msg.messageText}</div>}
            <span className="bubble-time">{formatMessageTime(msg.createdAt)}</span>
          </div>
        </div>
      );
    }

    // Patient's own messages right (me); doctor's messages left (them)
    const mine = msg.senderType === 'PATIENT';
    const who = mine ? 'me' : 'them';
    return (
      <div key={msg.messageId} className={'msg-row ' + who}>
        <div className="bubble">
          {!mine && !hasFile && <div className="bubble-name">{doctorName}</div>}
          {msg.messageText && <div className="bubble-text">{msg.messageText}</div>}
          {hasFile && (
            <div style={{ marginTop: msg.messageText ? 'var(--vc-space-2)' : 0 }}>
              {renderFileAttachment(msg, mine)}
            </div>
          )}
          <span className="bubble-time">{formatMessageTime(msg.createdAt)}</span>
        </div>
      </div>
    );
  };

  const renderThread = () => {
    if (loading) {
      return (
        <div className="ch-thread">
          <div className="sk-row"><div className="sk-bubble" style={{ width: '60%' }} /></div>
          <div className="sk-row right"><div className="sk-bubble" style={{ width: '45%' }} /></div>
          <div className="sk-row"><div className="sk-bubble" style={{ width: '70%' }} /></div>
          <div className="sk-row right"><div className="sk-bubble" style={{ width: '50%' }} /></div>
        </div>
      );
    }
    if (!messages || messages.length === 0) {
      return (
        <div className="ch-thread" style={{ justifyContent: 'center' }}>
          <EmptyState
            icon={<FeatherIcon icon="message-circle" size={26} />}
            title="Няма съобщения"
            description="Все още няма разменени съобщения в тази консултация."
          />
        </div>
      );
    }
    return (
      <div className="ch-thread" ref={messagesContainerRef}>
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </div>
    );
  };

  const renderComposer = () => {
    if (isRecording) {
      return (
        <div className="composer">
          <div className="composer-recording">
            <span className="rec-dot" />
            <span className="rec-timer">{formatRecordingTime(recordingDuration)}</span>
            <span className="rec-wave">
              {Array.from({ length: 18 }).map((_, i) => <span key={i} style={{ animationDelay: (i * 0.06) + 's' }} />)}
            </span>
            <button className="rec-stop" onClick={handleStopRecording}>
              <span style={{ width: 10, height: 10, background: 'currentColor', borderRadius: 2, display: 'inline-block' }} /> Спри
            </button>
          </div>
        </div>
      );
    }
    if (mediaBlobUrl) {
      return (
        <div className="composer">
          <div className="composer-recorded">
            <button className="rec-del" aria-label="Изтрий" onClick={handleCancelRecording} disabled={sending}>
              <FeatherIcon icon="trash" size={17} />
            </button>
            <div className="audio">
              <AudioPlayer src={mediaBlobUrl} style={{ color: 'var(--vc-color-primary)', width: '100%' }} />
            </div>
            <button className="rec-send" aria-label="Изпрати" onClick={handleSendVoiceMessage} disabled={sending}>
              {sending ? <span className="pch-spin"><FeatherIcon icon="loader" size={17} /></span> : <FeatherIcon icon="send" size={17} />}
            </button>
          </div>
        </div>
      );
    }
    const hasContent = newMessage.trim().length > 0 || selectedFile;
    return (
      <div className="composer" style={{ paddingBottom: 'max(var(--vc-space-3), env(safe-area-inset-bottom))' }}>
        {selectedFile && (
          <div className="composer-preview">
            <span className={'file-ico ' + fileKind(selectedFile.type, selectedFile.name)}>
              <FeatherIcon icon={fileIconName(fileKind(selectedFile.type, selectedFile.name))} size={16} />
            </span>
            <div className="file-meta">
              <span className="file-name">{selectedFile.name}</span>
              <span className="file-size">{formatFileSize(selectedFile.size)}</span>
            </div>
            <button className="rm" aria-label="Премахни" onClick={clearSelectedFile} disabled={sending}>
              <FeatherIcon icon="x" size={15} />
            </button>
          </div>
        )}
        <div className="composer-input-row">
          <button
            className="composer-attach"
            aria-label="Прикачи файл"
            onMouseDown={preventBlur}
            onClick={() => fileInputRef.current?.click()}
            disabled={sending}
          >
            <FeatherIcon icon="paperclip" size={18} />
          </button>
          <div className="composer-textwrap">
            <textarea
              ref={textAreaRef}
              className="composer-text"
              rows={1}
              placeholder="Текст..."
              value={newMessage}
              onChange={handleTextChange}
              onKeyDown={handleKeyDown}
              onFocus={handleInputFocus}
              maxLength={4000}
              disabled={sending}
            />
          </div>
          {hasContent ? (
            <button className="composer-send" aria-label="Изпрати" onMouseDown={preventBlur} onClick={handleSendMessage} disabled={sending}>
              {uploading ? <span className="pch-spin"><FeatherIcon icon="loader" size={18} /></span> : <FeatherIcon icon="send" size={18} />}
            </button>
          ) : (
            <button className="composer-mic" aria-label="Запиши глас" onMouseDown={preventBlur} onClick={handleStartRecording} disabled={sending}>
              <FeatherIcon icon="mic" size={18} />
            </button>
          )}
        </div>
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleFileSelect}
          accept={ALLOWED_EXTENSIONS.map((ext) => `.${ext}`).join(',')}
          style={{ display: 'none' }}
        />
      </div>
    );
  };

  return (
    <div>
      <Header />
      <Sidebar activeClassName="telechat" />
      <style>{CHAT_CSS}</style>

      <div className="page-wrapper">
        <div
          className="content"
          style={{ padding: 0, height: 'calc(100dvh - 70px)', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}
        >
          <div className="pch-outer">
            <div className="pch-surface">
              <div className="vc-chat">
                {/* Header */}
                <div className="ch-head">
                  <button className="ch-back" aria-label="Назад" onClick={() => navigate('/telechat')}>
                    <FeatherIcon icon="arrow-left" size={18} />
                  </button>
                  <span className="ch-avatar">{initials(doctorName)}</span>
                  <div className="ch-id">
                    <span className="ch-name">{doctorName}</span>
                    {doctorSpec && (
                      <div className="ch-sub">
                        <span className="ch-spec">{doctorSpec}</span>
                      </div>
                    )}
                  </div>
                  <span className={'ch-net ' + netState.cls} title={`Вашата връзка: ${netState.label}`}>
                    <span className="dot" />
                  </span>
                  {chatDetail && (
                    <span className="ch-pill">
                      <StatusPill tone={statusInfo.tone} size="sm">{statusInfo.label}</StatusPill>
                    </span>
                  )}
                </div>

                {/* State chrome */}
                {status === 'PENDING_APPROVAL' && (
                  <div className="ch-statebar">
                    <div className="banner warning">
                      <span className="b-ico"><FeatherIcon icon="clock" size={18} /></span>
                      <div className="b-body">
                        <div className="b-title">Заявката Ви чака одобрение от лекаря.</div>
                        <div className="b-desc">Ще можете да изпращате съобщения след като лекарят одобри консултацията.</div>
                      </div>
                    </div>
                  </div>
                )}
                {status === 'CLOSED' && (
                  <div className="ch-statebar">
                    <div className="banner info">
                      <span className="b-ico"><FeatherIcon icon="check-circle" size={18} /></span>
                      <div className="b-body">
                        <div className="b-title">Консултацията е приключена.</div>
                        {chatDetail?.closingNotes && <div className="b-desc">Бележки от лекаря: {chatDetail.closingNotes}</div>}
                      </div>
                    </div>
                  </div>
                )}
                {status === 'DECLINED' && (
                  <div className="ch-statebar">
                    <div className="banner danger">
                      <span className="b-ico"><FeatherIcon icon="x-circle" size={18} /></span>
                      <div className="b-body">
                        <div className="b-title">Консултацията е отказана.</div>
                        <div className="b-desc">
                          {chatDetail?.declineReason ? `Причина: ${chatDetail.declineReason}. ` : ''}Платената сума е възстановена.
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Thread */}
                {renderThread()}

                {/* Composer — ACTIVE only */}
                {canSendMessage && renderComposer()}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientChatDetail;
